import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useCatalog, formatPrice } from "@/lib/catalog";
import { PageHead, CardSkeleton, ErrorState, EmptyState } from "@/components/Ui";
import { Reveal } from "@/components/Reveal";
import { Check, Minus } from "lucide-react";

function PlanPrice({ plan, annual, lang, t }) {
  const value = annual ? plan.price_annual : plan.price_monthly;
  const formatted = formatPrice(value, plan.currency, lang);
  if (!formatted) {
    return <span className="font-display text-3xl font-extrabold text-marine">{t("pricing.custom")}</span>;
  }
  return (
    <span>
      <span className="font-display text-3xl font-extrabold text-marine">{formatted}</span>
      <span className="text-sm font-medium text-slate-400">{annual ? t("pricing.perYear") : t("pricing.perMonth")}</span>
    </span>
  );
}

export default function Pricing() {
  const { t, lang } = useLang();
  const { catalog, loading, error, reload } = useCatalog();
  const [annual, setAnnual] = useState(false);
  const plans = [...(catalog?.plans || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const cellPrice = (p, isAnnual) => formatPrice(isAnnual ? p.price_annual : p.price_monthly, p.currency, lang) || t("pricing.custom");
  const cellUsers = (p) => (p.max_users == null ? t("pricing.unlimited") : p.max_users);
  const cellModules = (p) =>
    p.included_modules?.includes("*") ? t("pricing.all") : `${p.included_modules?.length || 0}`;
  const rows = [
    { key: "priceM", label: t("pricing.f.priceM"), value: (p) => cellPrice(p, false) },
    { key: "priceA", label: t("pricing.f.priceA"), value: (p) => cellPrice(p, true) },
    { key: "users", label: t("pricing.f.users"), value: cellUsers },
    { key: "modules", label: t("pricing.f.modules"), value: cellModules },
    { key: "sector", label: t("pricing.f.sector"), value: (p) => (p.include_sector_modules ? t("pricing.included") : t("pricing.optional")) },
    { key: "trial", label: t("pricing.f.trial"), value: (p) => (p.trial_days ? t("pricing.days", { n: p.trial_days }) : "—") },
  ];

  return (
    <div className="bg-grid" data-testid="pricing-page">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <PageHead eyebrow={t("pricing.eyebrow")} title={t("pricing.title")} sub={t("pricing.sub")} testid="pricing-head" />

        <div className="mt-8 flex justify-center" data-testid="billing-toggle">
          <div className="flex rounded-lg border border-slate-200 bg-white p-1">
            {[
              { v: false, label: t("pricing.monthly") },
              { v: true, label: t("pricing.annual") },
            ].map(({ v, label }) => (
              <button
                key={label}
                onClick={() => setAnnual(v)}
                data-testid={`billing-${v ? "annual" : "monthly"}`}
                className={`rounded-md px-5 py-2 text-sm font-semibold transition-colors ${
                  annual === v ? "bg-marine text-white" : "text-slate-500 hover:text-marine"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <CardSkeleton count={5} />
          ) : error ? (
            <ErrorState onRetry={reload} />
          ) : plans.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-5" data-testid="plans-grid">
              {plans.map((p, i) => {
                const highlight = p.code === "professional";
                const allModules = p.included_modules?.includes("*");
                return (
                  <Reveal key={p.code} delay={i * 80}>
                    <div
                      className={`relative flex h-full flex-col rounded-xl border bg-white p-6 ${
                        highlight ? "border-gold shadow-xl shadow-gold/10 ring-1 ring-gold" : "border-slate-200"
                      }`}
                      data-testid={`plan-card-${p.code}`}
                    >
                      {highlight && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-3 py-1 text-xs font-bold uppercase tracking-wider text-marine" data-testid="plan-recommended-badge">
                          {t("pricing.recommended")}
                        </span>
                      )}
                      <h3 className="font-display text-lg font-bold text-marine">{p.name}</h3>
                      <div className="mt-3 min-h-[2.5rem]" data-testid={`plan-price-${p.code}`}>
                        <PlanPrice plan={p} annual={annual} lang={lang} t={t} />
                      </div>
                      {p.price_per_user_monthly != null && (
                        <p className="mt-1 text-xs text-slate-400">
                          {formatPrice(p.price_per_user_monthly, p.currency, lang)} {t("pricing.perUser")}
                        </p>
                      )}
                      <ul className="mt-5 flex-1 space-y-2.5 text-sm text-slate-600">
                        <li className="flex items-start gap-2">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                          {p.max_users == null ? t("pricing.usersUnlimited") : t("pricing.usersUpTo", { n: p.max_users })}
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                          {allModules ? t("pricing.allModules") : t("pricing.modulesIncluded", { n: p.included_modules?.length || 0 })}
                        </li>
                        <li className="flex items-start gap-2">
                          {p.include_sector_modules ? (
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                          ) : (
                            <Minus className="mt-0.5 h-4 w-4 shrink-0 text-slate-300" />
                          )}
                          {p.include_sector_modules ? t("pricing.sectorYes") : t("pricing.sectorNo")}
                        </li>
                        {p.trial_days != null && (
                          <li className="flex items-start gap-2">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                            {t("pricing.trial", { n: p.trial_days })}
                          </li>
                        )}
                      </ul>
                      <Link
                        to={p.is_custom ? "/contact" : "/demo"}
                        className={`${highlight ? "btn-gold" : "btn-primary"} mt-6 w-full`}
                        data-testid={`plan-cta-${p.code}`}
                      >
                        {p.is_custom ? t("pricing.contact") : t("pricing.choose")}
                      </Link>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>

        {!loading && !error && plans.length > 0 && (
          <Reveal className="mt-16">
            <h2 className="text-center font-display text-2xl font-bold text-marine sm:text-3xl">{t("pricing.compareTitle")}</h2>
            <div className="mg-card mt-8 overflow-x-auto" data-testid="comparison-table">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-marine text-white">
                    <th className="px-5 py-4 text-left font-display font-bold">{t("pricing.compareTitle")}</th>
                    {plans.map((p) => (
                      <th key={p.code} className={`px-5 py-4 text-center font-display font-bold ${p.code === "professional" ? "text-gold" : ""}`}>
                        {p.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={row.key} className={ri % 2 === 0 ? "bg-white" : "bg-mist"} data-testid={`compare-row-${row.key}`}>
                      <td className="px-5 py-3.5 font-medium text-marine">{row.label}</td>
                      {plans.map((p) => (
                        <td key={p.code} className={`px-5 py-3.5 text-center text-slate-600 ${p.code === "professional" ? "font-semibold text-marine" : ""}`}>
                          {row.value(p)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  );
}
